import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toMarketingPage } from "utils";
import {
  fetchPartner,
  fetchPartnerActivity,
  fetchPartnerBranches,
  fetchPartnerDevices,
  fetchPartnerPayments,
  fetchPartners,
  PARTNERS_LOAD_ERROR,
  reinstatePartner,
  savePartner,
  savePartnerBranch,
  sendBranchSignInEmail,
  suspendPartner,
} from "./thunkActions";

interface InitialState {
  isLoading: boolean;
  partners: IMarketingPartnerList | null;
  listError: string | null;
  listRequestId: string | null;
  isFetchingPartner: boolean;
  partner: IMarketingPartnerDetails | null;
  isFetchingDevices: boolean;
  devices: IPartnerDeviceList | null;
  devicesPartnerId: number | null;
  devicesRequestId: string | null;
  isFetchingBranches: boolean;
  branches: IPartnerBranchList | null;
  branchesPartnerId: number | null;
  isFetchingPayments: boolean;
  payments: IPartnerPaymentList | null;
  paymentsPartnerId: number | null;
  isFetchingActivity: boolean;
  activity: IPartnerActivityList | null;
  activityPartnerId: number | null;
}

const initialState: InitialState = {
  isLoading: false,
  partners: null,
  listError: null,
  listRequestId: null,
  isFetchingPartner: false,
  partner: null,
  isFetchingDevices: false,
  devices: null,
  devicesPartnerId: null,
  devicesRequestId: null,
  isFetchingBranches: false,
  branches: null,
  branchesPartnerId: null,
  isFetchingPayments: false,
  payments: null,
  paymentsPartnerId: null,
  isFetchingActivity: false,
  activity: null,
  activityPartnerId: null,
};

const updatePartnerStatus = (
  state: InitialState,
  result: IPartnerStatusResult
) => {
  if (state.partner && state.partner.id === result.id) {
    state.partner.status = result.status;
  }
};

export const partnersSlice = createSlice({
  name: "partners",
  initialState,
  reducers: {
    clearPartner: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.partner = null;
        state.devices = null;
        state.devicesPartnerId = null;
        state.devicesRequestId = null;
        state.branches = null;
        state.branchesPartnerId = null;
        state.payments = null;
        state.paymentsPartnerId = null;
        state.activity = null;
        state.activityPartnerId = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPartners.pending, (state, action) => {
        state.isLoading = true;
        state.listError = null;
        state.partners = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchPartners.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.partners = action.payload;
        state.isLoading = false;
        state.listRequestId = null;
      })
      .addCase(fetchPartners.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.isLoading = false;
        state.listRequestId = null;
        state.partners = null;
        state.listError = action.payload ?? PARTNERS_LOAD_ERROR;
      });

    builder
      .addCase(fetchPartner.pending, (state) => {
        state.isFetchingPartner = true;
      })
      .addCase(fetchPartner.fulfilled, (state, action) => {
        state.partner = action.payload;
        state.isFetchingPartner = false;
      })
      .addCase(fetchPartner.rejected, (state) => {
        state.isFetchingPartner = false;
      });

    builder.addCase(savePartner.fulfilled, (state, action) => {
      const { partner, branches, hasBranchError } = action.payload;
      state.partner = partner;

      if (branches) {
        state.branches = branches;
        state.branchesPartnerId = partner.id;
      } else if (hasBranchError) {
        state.branches = null;
        state.branchesPartnerId = null;
      } else if (state.branchesPartnerId !== partner.id) {
        state.branches = toMarketingPage<IPartnerBranch>([]);
        state.branchesPartnerId = partner.id;
      }
    });

    builder
      .addCase(fetchPartnerBranches.pending, (state) => {
        state.isFetchingBranches = true;
      })
      .addCase(fetchPartnerBranches.fulfilled, (state, action) => {
        state.branches = action.payload.list;
        state.branchesPartnerId = action.payload.id;
        state.isFetchingBranches = false;
      })
      .addCase(fetchPartnerBranches.rejected, (state) => {
        state.isFetchingBranches = false;
      });

    builder
      .addCase(fetchPartnerPayments.pending, (state) => {
        state.isFetchingPayments = true;
      })
      .addCase(fetchPartnerPayments.fulfilled, (state, action) => {
        state.payments = action.payload.list;
        state.paymentsPartnerId = action.payload.id;
        state.isFetchingPayments = false;
      })
      .addCase(fetchPartnerPayments.rejected, (state) => {
        state.isFetchingPayments = false;
      });

    builder
      .addCase(fetchPartnerDevices.pending, (state, action) => {
        state.devices = null;
        state.devicesPartnerId = action.meta.arg.id;
        state.devicesRequestId = action.meta.requestId;
        state.isFetchingDevices = true;
      })
      .addCase(fetchPartnerDevices.fulfilled, (state, action) => {
        if (state.devicesRequestId !== action.meta.requestId) return;
        state.devices = action.payload.list;
        state.devicesPartnerId = action.payload.id;
        state.devicesRequestId = null;
        state.isFetchingDevices = false;
      })
      .addCase(fetchPartnerDevices.rejected, (state, action) => {
        if (state.devicesRequestId !== action.meta.requestId) return;
        state.devicesRequestId = null;
        state.isFetchingDevices = false;
      });

    builder
      .addCase(fetchPartnerActivity.pending, (state) => {
        state.isFetchingActivity = true;
      })
      .addCase(fetchPartnerActivity.fulfilled, (state, action) => {
        state.activity = action.payload.list;
        state.activityPartnerId = action.payload.id;
        state.isFetchingActivity = false;
      })
      .addCase(fetchPartnerActivity.rejected, (state) => {
        state.isFetchingActivity = false;
      });

    builder
      .addCase(suspendPartner.fulfilled, (state, action) => {
        updatePartnerStatus(state, action.payload);
      })
      .addCase(reinstatePartner.fulfilled, (state, action) => {
        updatePartnerStatus(state, action.payload);
      });

    builder.addCase(savePartnerBranch.fulfilled, (state, action) => {
      const { id, branch } = action.payload;
      if (!state.branches || state.branchesPartnerId !== id) return;

      const index = state.branches.data.findIndex((item) => item.id === branch.id);

      if (index >= 0) {
        state.branches.data[index] = branch;
      } else {
        state.branches.data.push(branch);
        state.branches.total += 1;
      }
    });

    builder.addCase(sendBranchSignInEmail.fulfilled, (state, action) => {
      const { id, branchId, result } = action.payload;
      if (!state.branches || state.branchesPartnerId !== id) return;

      const branch = state.branches.data.find((item) => item.id === branchId);

      if (branch) {
        branch.sign_in_email_status = result.sign_in_email_status;
        branch.sign_in_email_sent_at = result.sign_in_email_sent_at;
      }
    });

  },
});

export default partnersSlice.reducer;
export const { clearPartner } = partnersSlice.actions;
export * from "./thunkActions";
